import axios from 'axios';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Video } from '../../../../../../dorian-shared/types/video/video';
import { api } from '../../../../../api';
import { Pagination } from '../../../../../ui/Pagination/Pagination';
import {
  DEFAULT_VIDEO_ORDER,
  VideoOrder,
  VideoSortDropdown,
} from '../../../../VideoLibraryPage/VideoLibraryContainer/VideosProvider/videoSort';
import classes from './VideoTrackPicker.module.scss';

// Tweak this to experiment with episode video picker page size.
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type VideoTrackPickerProps = {
  selectedId?: number;
  selectedTitle?: string;
  disabled?: boolean;
  onSelect: (video: Video) => void;
};

export function VideoTrackPicker({
  selectedId,
  selectedTitle,
  disabled,
  onSelect,
}: VideoTrackPickerProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<VideoOrder>(DEFAULT_VIDEO_ORDER);
  const [results, setResults] = useState<Video[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, order]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (wrapRef.current?.contains(target)) {
        return;
      }
      if (target instanceof Element && target.closest('[data-video-sort-menu]')) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const fetchPage = useCallback(() => {
    if (!open) {
      return undefined;
    }
    const cancelToken = axios.CancelToken.source();
    setLoading(true);
    api.get('/v1/video', {
      params: {
        search: debouncedSearch || undefined,
        order,
        limit: PAGE_SIZE,
        offset: PAGE_SIZE * (page - 1),
      },
      cancelToken: cancelToken.token,
    }).then((res) => {
      setResults(res.data.videos || []);
      setTotalCount(Number(res.data.totalCount) || 0);
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        setResults([]);
        setTotalCount(0);
      }
    }).finally(() => setLoading(false));
    return () => cancelToken.cancel('Video catalog unmount');
  }, [open, debouncedSearch, order, page]);

  useEffect(() => fetchPage(), [fetchPage]);

  const handleSelect = (video: Video) => {
    onSelect(video);
    setOpen(false);
  };

  const trackLabel = selectedTitle || (selectedId ? 'Video' : 'Select a video');

  return (
    <div className={classes.wrap} ref={wrapRef}>
      <Button
        variant="outline-secondary"
        size="sm"
        className={`${classes.trackButton}${selectedId ? '' : ` ${classes.trackButtonPlaceholder}`}`}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        {trackLabel}
      </Button>
      {open && (
        <div className={classes.panel}>
          <div className={classes.search}>
            <Form.Control
              className={classes.searchInput}
              size="sm"
              placeholder="Search videos"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div className={classes.filters}>
            <VideoSortDropdown
              order={order}
              onChange={setOrder}
              className={classes.sort}
              toggleClassName={classes.sortToggle}
            />
          </div>
          <div className={classes.list}>
            {loading && results.length === 0 && (
              <div className={classes.loading}>
                <Spinner animation="border" size="sm" />
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className={classes.empty}>No videos match.</div>
            )}
            {results.map((video) => (
              <div
                key={video.id}
                className={`${classes.item} ${selectedId === video.id ? classes.itemSelected : ''}`}
              >
                <button
                  type="button"
                  className={classes.itemMain}
                  onClick={() => handleSelect(video)}
                >
                  <span className={classes.itemTitle}>{video.title}</span>
                </button>
              </div>
            ))}
          </div>
          <div className={classes.pager}>
            <Pagination
              className="pagination-bar mb-0"
              currentPage={page}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              nearbyCount={1}
            />
          </div>
        </div>
      )}
    </div>
  );
}
